import { SOCIAL_PLATFORM_IDS, type SocialPlatformId } from "@dream/config/constants";
import type { ConnectField, PlatformDefinition, SettingField, SettingOption } from "./types.ts";

function opt(value: string, label: string): SettingOption {
  return { value, label };
}

function select(
  key: string,
  label: string,
  options: SettingOption[],
  defaultValue: string,
  extra: Partial<SettingField> = {},
): SettingField {
  return { key, label, type: "select", options, defaultValue, ...extra };
}

function toggle(
  key: string,
  label: string,
  defaultValue = false,
  extra: Partial<SettingField> = {},
): SettingField {
  return { key, label, type: "boolean", defaultValue, ...extra };
}

function text(key: string, label: string, extra: Partial<SettingField> = {}): SettingField {
  return { key, label, type: "string", ...extra };
}

function area(key: string, label: string, extra: Partial<SettingField> = {}): SettingField {
  return { key, label, type: "text", ...extra };
}

function tokenFields(extra: ConnectField[] = []): ConnectField[] {
  return [
    {
      key: "accessToken",
      label: "Access token",
      type: "password",
      required: true,
      help: "Token da API oficial da rede. Nunca é exibido depois de salvo.",
    },
    {
      key: "refreshToken",
      label: "Refresh token",
      type: "password",
      required: false,
    },
    ...extra,
  ];
}

const WEEKDAY_SLOTS = [
  { weekday: 1, time: "09:00" },
  { weekday: 3, time: "12:00" },
  { weekday: 5, time: "18:00" },
];

const WHO_CAN_REPLY: SettingOption[] = [
  opt("everyone", "Todos"),
  opt("following", "Quem você segue"),
  opt("mentioned", "Contas mencionadas"),
  opt("subscribers", "Assinantes"),
  opt("verified", "Contas verificadas"),
];

/**
 * Catálogo das 34 redes.
 *
 * Regras, limites e campos vêm da documentação pública de cada plataforma e da
 * documentação pública do Postiz (nomes de API e limites). Nenhuma linha de
 * código AGPL foi copiada.
 */
export const PLATFORMS: Record<SocialPlatformId, PlatformDefinition> = {
  x: {
    id: "x",
    name: "X",
    apiType: "x",
    category: "social",
    color: "#0f1419",
    charLimit: 280,
    editor: "html",
    thread: "thread",
    commentsMedia: true,
    analytics: true,
    needsDeveloperApp: true,
    connectMethods: ["sandbox", "oauth", "credentials"],
    oauth: {
      authorizeUrl: "https://twitter.com/i/oauth2/authorize",
      tokenUrl: "https://api.x.com/2/oauth2/token",
      scopes: ["tweet.read", "tweet.write", "users.read", "offline.access", "media.write"],
      pkce: true,
    },
    credentialFields: tokenFields(),
    media: {
      minAttachments: 0,
      maxAttachments: 4,
      images: true,
      videos: true,
      mixed: false,
      textOnly: true,
      maxVideos: 1,
      note: "Até 4 imagens ou 1 vídeo. Artigo: só imagens, título obrigatório.",
    },
    settings: [
      select("postType", "Tipo", [opt("post", "Post"), opt("article", "Artigo")], "post"),
      text("title", "Título do artigo", {
        required: true,
        maxLength: 200,
        when: { field: "postType", equals: "article" },
      }),
      select("whoCanReply", "Quem pode responder", WHO_CAN_REPLY, "everyone"),
      text("communityId", "ID da comunidade"),
      toggle("madeWithAi", "Feito com IA"),
      toggle("paidPartnership", "Parceria paga"),
    ],
    defaultSlots: WEEKDAY_SLOTS,
  },

  linkedin: {
    id: "linkedin",
    name: "LinkedIn",
    apiType: "linkedin",
    category: "social",
    color: "#0a66c2",
    charLimit: 3000,
    editor: "standard",
    thread: "comment",
    commentsMedia: false,
    analytics: false,
    needsDeveloperApp: true,
    connectMethods: ["sandbox", "oauth", "credentials"],
    oauth: {
      authorizeUrl: "https://www.linkedin.com/oauth/v2/authorization",
      tokenUrl: "https://www.linkedin.com/oauth/v2/accessToken",
      scopes: ["openid", "profile", "w_member_social", "email"],
      pkce: false,
    },
    credentialFields: tokenFields(),
    media: {
      minAttachments: 0,
      maxAttachments: 20,
      images: true,
      videos: true,
      mixed: false,
      textOnly: true,
      maxVideos: 1,
      note: "Carrossel: pelo menos 2 imagens e nenhum vídeo (vira PDF).",
    },
    settings: [
      toggle("carousel", "Publicar como carrossel"),
      text("firstComment", "Primeiro comentário", { maxLength: 1250 }),
    ],
    defaultSlots: WEEKDAY_SLOTS,
  },

  "linkedin-page": {
    id: "linkedin-page",
    name: "LinkedIn Page",
    apiType: "linkedin-page",
    category: "social",
    color: "#004182",
    charLimit: 3000,
    editor: "standard",
    thread: "comment",
    commentsMedia: false,
    analytics: true,
    needsDeveloperApp: true,
    connectMethods: ["sandbox", "oauth", "credentials"],
    oauth: {
      authorizeUrl: "https://www.linkedin.com/oauth/v2/authorization",
      tokenUrl: "https://www.linkedin.com/oauth/v2/accessToken",
      scopes: ["openid", "profile", "w_organization_social", "r_organization_social", "email"],
      pkce: false,
    },
    credentialFields: tokenFields([
      {
        key: "organizationUrn",
        label: "URN da página",
        type: "string",
        required: true,
        placeholder: "urn:li:organization:…",
      },
    ]),
    media: {
      minAttachments: 0,
      maxAttachments: 20,
      images: true,
      videos: true,
      mixed: false,
      textOnly: true,
      maxVideos: 1,
      note: "Carrossel: pelo menos 2 imagens e nenhum vídeo.",
    },
    settings: [
      toggle("carousel", "Publicar como carrossel"),
      text("firstComment", "Primeiro comentário", { maxLength: 1250 }),
    ],
    defaultSlots: WEEKDAY_SLOTS,
  },

  reddit: {
    id: "reddit",
    name: "Reddit",
    apiType: "reddit",
    category: "community",
    color: "#ff4500",
    charLimit: 10_000,
    editor: "markdown",
    thread: "comment",
    commentsMedia: true,
    analytics: false,
    needsDeveloperApp: true,
    connectMethods: ["sandbox", "oauth", "credentials"],
    oauth: {
      authorizeUrl: "https://www.reddit.com/api/v1/authorize",
      tokenUrl: "https://www.reddit.com/api/v1/access_token",
      scopes: ["identity", "submit", "read", "flair"],
      pkce: false,
      extraAuthorizeParams: { duration: "permanent" },
    },
    credentialFields: tokenFields(),
    media: {
      minAttachments: 0,
      maxAttachments: 1,
      images: true,
      videos: true,
      mixed: false,
      textOnly: true,
      note: "Post de mídia exige exatamente 1 anexo. Vídeo precisa de thumbnail.",
    },
    settings: [
      text("subreddit", "Subreddit", { required: true, placeholder: "nome_sem_r/" }),
      text("title", "Título", { required: true, maxLength: 300 }),
      select(
        "postKind",
        "Tipo",
        [
          opt("self", "Texto"),
          opt("link", "Link"),
          opt("image", "Imagem"),
          opt("video", "Vídeo"),
          opt("videogif", "GIF"),
        ],
        "self",
      ),
      text("url", "URL", { when: { field: "postKind", equals: "link" }, required: true }),
      text("flairId", "Flair"),
      text("flairText", "Texto da flair"),
      toggle("nsfw", "NSFW"),
      toggle("spoiler", "Spoiler"),
      toggle("sendReplies", "Receber respostas na caixa de entrada", true),
    ],
    defaultSlots: WEEKDAY_SLOTS,
  },

  instagram: {
    id: "instagram",
    name: "Instagram",
    apiType: "instagram",
    category: "social",
    color: "#e4405f",
    charLimit: 2200,
    editor: "standard",
    thread: "comment",
    commentsMedia: false,
    analytics: true,
    needsDeveloperApp: true,
    connectMethods: ["sandbox", "oauth", "credentials"],
    oauth: {
      authorizeUrl: "https://www.facebook.com/v21.0/dialog/oauth",
      tokenUrl: "https://graph.facebook.com/v21.0/oauth/access_token",
      scopes: [
        "instagram_basic",
        "instagram_content_publish",
        "pages_show_list",
        "pages_read_engagement",
        "business_management",
      ],
      pkce: false,
    },
    credentialFields: tokenFields([
      {
        key: "igUserId",
        label: "ID da conta Instagram Business",
        type: "string",
        required: true,
      },
    ]),
    media: {
      minAttachments: 1,
      maxAttachments: 10,
      images: true,
      videos: true,
      mixed: false,
      textOnly: false,
      note: "Pelo menos 1 anexo. Story: 1 imagem. Trial reel: exatamente 1 vídeo.",
    },
    settings: [
      select(
        "postType",
        "Formato",
        [
          opt("post", "Feed"),
          opt("reel", "Reel"),
          opt("story", "Story"),
          opt("trial_reel", "Trial reel"),
        ],
        "post",
      ),
      text("firstComment", "Primeiro comentário", { maxLength: 2200 }),
      text("collaborators", "Colaboradores (usuários, separados por vírgula)"),
      text("audioId", "ID do áudio (só reel de um vídeo)"),
      text("locationId", "ID de localização"),
      text("userTags", "Marcações (usuários)"),
      toggle("shareToFeed", "Também no feed", true, {
        when: { field: "postType", equals: "reel" },
      }),
    ],
    defaultSlots: [
      { weekday: 2, time: "11:00" },
      { weekday: 4, time: "18:00" },
      { weekday: 6, time: "10:00" },
    ],
  },

  "instagram-standalone": {
    id: "instagram-standalone",
    name: "Instagram (Standalone)",
    apiType: "instagram-standalone",
    category: "social",
    color: "#c13584",
    charLimit: 2200,
    editor: "standard",
    thread: "comment",
    commentsMedia: false,
    analytics: true,
    needsDeveloperApp: true,
    connectMethods: ["sandbox", "oauth", "credentials"],
    oauth: {
      authorizeUrl: "https://www.instagram.com/oauth/authorize",
      tokenUrl: "https://api.instagram.com/oauth/access_token",
      scopes: ["instagram_business_basic", "instagram_business_content_publish"],
      pkce: false,
    },
    credentialFields: tokenFields([
      { key: "igUserId", label: "ID da conta Instagram", type: "string", required: true },
    ]),
    media: {
      minAttachments: 1,
      maxAttachments: 10,
      images: true,
      videos: true,
      mixed: false,
      textOnly: false,
      note: "Pelo menos 1 anexo. Story: 1 imagem. Trial reel: 1 vídeo.",
    },
    settings: [
      select(
        "postType",
        "Formato",
        [opt("post", "Feed"), opt("reel", "Reel"), opt("story", "Story")],
        "post",
      ),
      text("firstComment", "Primeiro comentário"),
    ],
    defaultSlots: [
      { weekday: 2, time: "11:00" },
      { weekday: 4, time: "18:00" },
    ],
  },

  facebook: {
    id: "facebook",
    name: "Facebook Page",
    apiType: "facebook",
    category: "social",
    color: "#1877f2",
    charLimit: 63_206,
    editor: "standard",
    thread: "comment",
    commentsMedia: true,
    analytics: true,
    needsDeveloperApp: true,
    connectMethods: ["sandbox", "oauth", "credentials"],
    oauth: {
      authorizeUrl: "https://www.facebook.com/v21.0/dialog/oauth",
      tokenUrl: "https://graph.facebook.com/v21.0/oauth/access_token",
      scopes: [
        "pages_manage_posts",
        "pages_read_engagement",
        "pages_show_list",
        "business_management",
      ],
      pkce: false,
    },
    credentialFields: tokenFields([
      { key: "pageId", label: "ID da Página", type: "string", required: true },
    ]),
    media: {
      minAttachments: 0,
      maxAttachments: 10,
      images: true,
      videos: true,
      mixed: false,
      textOnly: true,
      note: "Story exige anexo; cada anexo vira um story. Fundo colorido: texto ≤ 130.",
    },
    settings: [
      select(
        "postType",
        "Formato",
        [opt("post", "Post"), opt("story", "Story"), opt("reel", "Reel")],
        "post",
      ),
      select(
        "backgroundPreset",
        "Fundo colorido",
        [
          opt("", "Nenhum"),
          opt("solid_blue", "Azul"),
          opt("solid_red", "Vermelho"),
          opt("solid_green", "Verde"),
          opt("solid_purple", "Roxo"),
          opt("gradient_sunset", "Pôr do sol"),
        ],
        "",
      ),
      text("firstComment", "Primeiro comentário"),
      text("placeId", "ID do local"),
      toggle("published", "Publicar imediatamente", true),
    ],
    defaultSlots: WEEKDAY_SLOTS,
  },

  threads: {
    id: "threads",
    name: "Threads",
    apiType: "threads",
    category: "social",
    color: "#000000",
    charLimit: 500,
    editor: "standard",
    thread: "thread",
    commentsMedia: true,
    analytics: true,
    needsDeveloperApp: true,
    connectMethods: ["sandbox", "oauth", "credentials"],
    oauth: {
      authorizeUrl: "https://threads.net/oauth/authorize",
      tokenUrl: "https://graph.threads.net/oauth/access_token",
      scopes: ["threads_basic", "threads_content_publish"],
      pkce: false,
    },
    credentialFields: tokenFields(),
    media: {
      minAttachments: 0,
      maxAttachments: 10,
      images: true,
      videos: true,
      mixed: false,
      textOnly: true,
      note: "Texto e/ou mídia. Threads longos viram replies encadeados.",
    },
    settings: [
      select(
        "replyControl",
        "Quem pode responder",
        [
          opt("everyone", "Todos"),
          opt("accounts_you_follow", "Quem você segue"),
          opt("mentioned_only", "Mencionados"),
        ],
        "everyone",
      ),
      text("topicTag", "Tópico"),
    ],
    defaultSlots: WEEKDAY_SLOTS,
  },

  youtube: {
    id: "youtube",
    name: "YouTube",
    apiType: "youtube",
    category: "video",
    color: "#ff0000",
    charLimit: 5000,
    editor: "plain",
    thread: "none",
    commentsMedia: false,
    analytics: true,
    needsDeveloperApp: true,
    connectMethods: ["sandbox", "oauth", "credentials"],
    oauth: {
      authorizeUrl: "https://accounts.google.com/o/oauth2/v2/auth",
      tokenUrl: "https://oauth2.googleapis.com/token",
      scopes: [
        "https://www.googleapis.com/auth/youtube.upload",
        "https://www.googleapis.com/auth/youtube",
      ],
      pkce: false,
      extraAuthorizeParams: { access_type: "offline", prompt: "consent" },
    },
    credentialFields: tokenFields(),
    media: {
      minAttachments: 1,
      maxAttachments: 1,
      images: false,
      videos: true,
      mixed: false,
      textOnly: false,
      maxVideos: 1,
      note: "Exatamente 1 vídeo. Thumbnail à parte.",
    },
    settings: [
      text("title", "Título", { required: true, maxLength: 100 }),
      select(
        "privacy",
        "Privacidade",
        [opt("public", "Público"), opt("unlisted", "Não listado"), opt("private", "Privado")],
        "public",
      ),
      toggle("madeForKids", "Feito para crianças"),
      text("tags", "Tags (separadas por vírgula)", {
        help: "Soma das tags ≤ 500 caracteres.",
      }),
      text("categoryId", "ID da categoria"),
      text("playlistId", "ID da playlist"),
      text("thumbnailUrl", "URL da thumbnail"),
      toggle("notifySubscribers", "Notificar inscritos", true),
      toggle("embeddable", "Permitir embed", true),
      select(
        "license",
        "Licença",
        [opt("youtube", "YouTube padrão"), opt("creativeCommon", "Creative Commons")],
        "youtube",
      ),
      toggle("selfDeclaredMadeForKids", "Autodeclaração made for kids"),
    ],
    defaultSlots: [{ weekday: 4, time: "18:00" }],
  },

  gmb: {
    id: "gmb",
    name: "Google Business",
    apiType: "gmb",
    category: "other",
    color: "#4285f4",
    charLimit: 1500,
    editor: "plain",
    thread: "none",
    commentsMedia: false,
    analytics: false,
    needsDeveloperApp: true,
    connectMethods: ["sandbox", "oauth", "credentials"],
    oauth: {
      authorizeUrl: "https://accounts.google.com/o/oauth2/v2/auth",
      tokenUrl: "https://oauth2.googleapis.com/token",
      scopes: ["https://www.googleapis.com/auth/business.manage"],
      pkce: false,
      extraAuthorizeParams: { access_type: "offline", prompt: "consent" },
    },
    credentialFields: tokenFields([
      { key: "locationId", label: "ID da localização", type: "string", required: true },
    ]),
    media: {
      minAttachments: 0,
      maxAttachments: 1,
      images: true,
      videos: false,
      mixed: false,
      textOnly: true,
      note: "No máximo 1 imagem. Vídeo não é suportado.",
    },
    settings: [
      select(
        "postType",
        "Tipo",
        [opt("update", "Atualização"), opt("event", "Evento"), opt("offer", "Oferta")],
        "update",
      ),
      text("title", "Título", { when: { field: "postType", equals: "event" }, required: true }),
      text("eventStart", "Início", {
        type: "datetime",
        when: { field: "postType", equals: "event" },
        required: true,
      }),
      text("eventEnd", "Fim", {
        type: "datetime",
        when: { field: "postType", equals: "event" },
        required: true,
      }),
      text("couponCode", "Cupom", { when: { field: "postType", equals: "offer" } }),
      text("redeemUrl", "URL de resgate", {
        type: "url",
        when: { field: "postType", equals: "offer" },
      }),
      area("terms", "Termos", { when: { field: "postType", equals: "offer" } }),
      select(
        "cta",
        "Chamada para ação",
        [
          opt("", "Nenhuma"),
          opt("book", "Reservar"),
          opt("order", "Pedir"),
          opt("shop", "Comprar"),
          opt("learn_more", "Saiba mais"),
          opt("sign_up", "Inscrever-se"),
          opt("call", "Ligar"),
        ],
        "",
      ),
      text("ctaUrl", "URL da CTA", { type: "url" }),
    ],
    defaultSlots: WEEKDAY_SLOTS,
  },

  tiktok: {
    id: "tiktok",
    name: "TikTok",
    apiType: "tiktok",
    category: "video",
    color: "#010101",
    charLimit: 2200,
    editor: "plain",
    thread: "none",
    commentsMedia: false,
    analytics: true,
    needsDeveloperApp: true,
    connectMethods: ["sandbox", "oauth", "credentials"],
    oauth: {
      authorizeUrl: "https://www.tiktok.com/v2/auth/authorize/",
      tokenUrl: "https://open.tiktokapis.com/v2/oauth/token/",
      scopes: ["user.info.basic", "video.upload", "video.publish"],
      pkce: false,
    },
    credentialFields: tokenFields([
      { key: "openId", label: "Open ID", type: "string", required: false },
    ]),
    media: {
      minAttachments: 1,
      maxAttachments: 35,
      images: true,
      videos: true,
      mixed: false,
      textOnly: false,
      note: "Pelo menos 1 anexo. Um vídeo ou várias fotos, nunca misturado.",
    },
    settings: [
      select(
        "method",
        "Método",
        [opt("direct_post", "Publicar direto"), opt("upload", "Enviar para o app (inbox 24h)")],
        "direct_post",
      ),
      text("title", "Título", { maxLength: 90 }),
      select(
        "privacyLevel",
        "Privacidade",
        [
          opt("PUBLIC_TO_EVERYONE", "Público"),
          opt("MUTUAL_FOLLOW_FRIENDS", "Amigos"),
          opt("SELF_ONLY", "Somente eu"),
        ],
        "PUBLIC_TO_EVERYONE",
      ),
      toggle("disableComments", "Desativar comentários"),
      toggle("disableDuet", "Desativar dueto"),
      toggle("disableStitch", "Desativar stitch"),
      toggle("autoAddMusic", "Adicionar música automaticamente"),
      toggle("brandedContent", "Conteúdo de marca"),
      toggle("aiGenerated", "Conteúdo gerado por IA"),
    ],
    defaultSlots: [
      { weekday: 2, time: "19:00" },
      { weekday: 4, time: "19:00" },
      { weekday: 6, time: "11:00" },
    ],
  },

  pinterest: {
    id: "pinterest",
    name: "Pinterest",
    apiType: "pinterest",
    category: "social",
    color: "#e60023",
    charLimit: 500,
    editor: "plain",
    thread: "none",
    commentsMedia: false,
    analytics: true,
    needsDeveloperApp: true,
    connectMethods: ["sandbox", "oauth", "credentials"],
    oauth: {
      authorizeUrl: "https://www.pinterest.com/oauth/",
      tokenUrl: "https://api.pinterest.com/v5/oauth/token",
      scopes: ["boards:read", "pins:read", "pins:write"],
      pkce: false,
    },
    credentialFields: tokenFields(),
    media: {
      minAttachments: 1,
      maxAttachments: 5,
      images: true,
      videos: true,
      mixed: true,
      textOnly: false,
      note: "1 a 5 imagens. Pin de vídeo precisa do vídeo e de uma capa.",
    },
    settings: [
      text("boardId", "ID do board", { required: true }),
      text("title", "Título", { required: true, maxLength: 100 }),
      text("link", "Link de destino", { type: "url" }),
      text("dominantColor", "Cor dominante (hex)"),
      text("altText", "Texto alternativo"),
    ],
    defaultSlots: WEEKDAY_SLOTS,
  },

  dribbble: {
    id: "dribbble",
    name: "Dribbble",
    apiType: "dribbble",
    category: "community",
    color: "#ea4c89",
    charLimit: 40_000,
    editor: "plain",
    thread: "comment",
    commentsMedia: true,
    analytics: false,
    needsDeveloperApp: true,
    connectMethods: ["sandbox", "oauth", "credentials"],
    oauth: {
      authorizeUrl: "https://dribbble.com/oauth/authorize",
      tokenUrl: "https://dribbble.com/oauth/token",
      scopes: ["public", "upload"],
      pkce: false,
    },
    credentialFields: tokenFields(),
    media: {
      minAttachments: 1,
      maxAttachments: 1,
      images: true,
      videos: false,
      mixed: false,
      textOnly: false,
      note: "Exatamente 1 imagem.",
    },
    settings: [
      text("title", "Título", { required: true, maxLength: 64 }),
      text("tags", "Tags (máx. 12, separadas por vírgula)"),
    ],
    defaultSlots: WEEKDAY_SLOTS,
  },

  discord: {
    id: "discord",
    name: "Discord",
    apiType: "discord",
    category: "messaging",
    color: "#5865f2",
    charLimit: 1980,
    editor: "markdown",
    thread: "comment",
    commentsMedia: true,
    analytics: false,
    needsDeveloperApp: true,
    connectMethods: ["sandbox", "oauth", "credentials"],
    oauth: {
      authorizeUrl: "https://discord.com/oauth2/authorize",
      tokenUrl: "https://discord.com/api/oauth2/token",
      scopes: ["identify", "guilds", "webhook.incoming"],
      pkce: false,
    },
    credentialFields: [
      {
        key: "webhookUrl",
        label: "URL do webhook",
        type: "url",
        required: true,
        help: "Canal → Integrações → Webhooks. Preferível ao bot OAuth para self-host.",
      },
      {
        key: "botToken",
        label: "Token do bot (opcional)",
        type: "password",
        required: false,
      },
    ],
    media: {
      minAttachments: 0,
      maxAttachments: 10,
      images: true,
      videos: true,
      mixed: true,
      textOnly: true,
      note: "Texto, arquivos ou embed.",
    },
    settings: [
      text("channelId", "ID do canal"),
      text("username", "Nome de exibição do webhook"),
      text("avatarUrl", "Avatar do webhook", { type: "url" }),
    ],
    defaultSlots: WEEKDAY_SLOTS,
  },

  slack: {
    id: "slack",
    name: "Slack",
    apiType: "slack",
    category: "messaging",
    color: "#4a154b",
    charLimit: 400_000,
    editor: "markdown",
    thread: "comment",
    commentsMedia: true,
    analytics: false,
    needsDeveloperApp: true,
    connectMethods: ["sandbox", "oauth", "credentials"],
    oauth: {
      authorizeUrl: "https://slack.com/oauth/v2/authorize",
      tokenUrl: "https://slack.com/api/oauth.v2.access",
      scopes: ["chat:write", "chat:write.public", "files:write"],
      pkce: false,
    },
    credentialFields: [
      {
        key: "webhookUrl",
        label: "Incoming webhook",
        type: "url",
        required: false,
      },
      {
        key: "accessToken",
        label: "Bot token (xoxb-…)",
        type: "password",
        required: false,
        help: "Informe o webhook ou o token do bot.",
      },
    ],
    media: {
      minAttachments: 0,
      maxAttachments: 10,
      images: true,
      videos: true,
      mixed: true,
      textOnly: true,
      note: "Mensagem e arquivos no canal escolhido.",
    },
    settings: [text("channelId", "Canal", { required: true, placeholder: "#marketing ou ID" })],
    defaultSlots: WEEKDAY_SLOTS,
  },

  kick: {
    id: "kick",
    name: "Kick",
    apiType: "kick",
    category: "video",
    color: "#53fc18",
    charLimit: 500,
    editor: "plain",
    thread: "comment",
    commentsMedia: false,
    analytics: false,
    needsDeveloperApp: true,
    connectMethods: ["sandbox", "oauth", "credentials"],
    oauth: {
      authorizeUrl: "https://id.kick.com/oauth/authorize",
      tokenUrl: "https://id.kick.com/oauth/token",
      scopes: ["user:read", "channel:read", "chat:write"],
      pkce: true,
    },
    credentialFields: tokenFields(),
    media: {
      minAttachments: 0,
      maxAttachments: 1,
      images: true,
      videos: false,
      mixed: false,
      textOnly: true,
      note: "Mensagem de chat; comentários sem mídia.",
    },
    settings: [text("channelSlug", "Canal")],
    defaultSlots: WEEKDAY_SLOTS,
  },

  twitch: {
    id: "twitch",
    name: "Twitch",
    apiType: "twitch",
    category: "video",
    color: "#9146ff",
    charLimit: 500,
    editor: "plain",
    thread: "comment",
    commentsMedia: false,
    analytics: false,
    needsDeveloperApp: true,
    connectMethods: ["sandbox", "oauth", "credentials"],
    oauth: {
      authorizeUrl: "https://id.twitch.tv/oauth2/authorize",
      tokenUrl: "https://id.twitch.tv/oauth2/token",
      scopes: ["user:write:chat", "user:read:email", "chat:edit", "chat:read"],
      pkce: false,
    },
    credentialFields: tokenFields([
      { key: "broadcasterId", label: "Broadcaster ID", type: "string", required: false },
    ]),
    media: {
      minAttachments: 0,
      maxAttachments: 0,
      images: false,
      videos: false,
      mixed: false,
      textOnly: true,
      note: "Somente texto no chat. Comentários sem mídia.",
    },
    settings: [text("broadcasterId", "Broadcaster ID")],
    defaultSlots: WEEKDAY_SLOTS,
  },

  mastodon: {
    id: "mastodon",
    name: "Mastodon",
    apiType: "mastodon",
    category: "social",
    color: "#6364ff",
    charLimit: 500,
    editor: "plain",
    thread: "thread",
    commentsMedia: true,
    analytics: false,
    needsDeveloperApp: false,
    connectMethods: ["sandbox", "oauth", "credentials"],
    oauth: {
      authorizeUrl: "{instance}/oauth/authorize",
      tokenUrl: "{instance}/oauth/token",
      scopes: ["read", "write:statuses", "write:media"],
      pkce: false,
    },
    credentialFields: [
      {
        key: "instanceUrl",
        label: "URL da instância",
        type: "url",
        required: true,
        placeholder: "https://mastodon.social",
      },
      {
        key: "accessToken",
        label: "Access token",
        type: "password",
        required: true,
      },
    ],
    media: {
      minAttachments: 0,
      maxAttachments: 4,
      images: true,
      videos: true,
      mixed: true,
      textOnly: true,
      note: "Até 4 anexos. Sem settings extras além de conteúdo e mídia.",
    },
    settings: [
      select(
        "visibility",
        "Visibilidade",
        [
          opt("public", "Público"),
          opt("unlisted", "Não listado"),
          opt("private", "Seguidores"),
          opt("direct", "Direto"),
        ],
        "public",
      ),
      toggle("sensitive", "Marcar como sensível"),
      text("spoilerText", "Aviso de conteúdo"),
      text("language", "Idioma (ISO)", { defaultValue: "pt" }),
    ],
    defaultSlots: WEEKDAY_SLOTS,
  },

  bluesky: {
    id: "bluesky",
    name: "Bluesky",
    apiType: "bluesky",
    category: "social",
    color: "#1185fe",
    charLimit: 300,
    editor: "plain",
    thread: "thread",
    commentsMedia: true,
    analytics: false,
    needsDeveloperApp: false,
    connectMethods: ["sandbox", "credentials"],
    credentialFields: [
      {
        key: "identifier",
        label: "Handle ou e-mail",
        type: "string",
        required: true,
        placeholder: "voce.bsky.social",
      },
      {
        key: "appPassword",
        label: "App password",
        type: "password",
        required: true,
        help: "Settings → App passwords no Bluesky. Não use a senha da conta.",
      },
      {
        key: "serviceUrl",
        label: "PDS",
        type: "url",
        required: false,
        placeholder: "https://bsky.social",
      },
    ],
    media: {
      minAttachments: 0,
      maxAttachments: 4,
      images: true,
      videos: true,
      mixed: false,
      textOnly: true,
      maxVideos: 1,
      note: "Até 4 imagens ou 1 vídeo.",
    },
    settings: [text("langs", "Idiomas (ISO, vírgula)", { defaultValue: "pt" })],
    defaultSlots: WEEKDAY_SLOTS,
  },

  lemmy: {
    id: "lemmy",
    name: "Lemmy",
    apiType: "lemmy",
    category: "community",
    color: "#00c853",
    charLimit: 10_000,
    editor: "markdown",
    thread: "comment",
    commentsMedia: true,
    analytics: false,
    needsDeveloperApp: false,
    connectMethods: ["sandbox", "credentials"],
    credentialFields: [
      {
        key: "instanceUrl",
        label: "Instância",
        type: "url",
        required: true,
        placeholder: "https://lemmy.world",
      },
      { key: "username", label: "Usuário", type: "string", required: true },
      { key: "password", label: "Senha", type: "password", required: true },
    ],
    media: {
      minAttachments: 0,
      maxAttachments: 1,
      images: true,
      videos: false,
      mixed: false,
      textOnly: true,
      note: "Post de comunidade: título + corpo markdown.",
    },
    settings: [
      text("community", "Comunidade", { required: true }),
      text("title", "Título", { required: true, maxLength: 200 }),
      text("url", "URL", { type: "url" }),
      toggle("nsfw", "NSFW"),
    ],
    defaultSlots: WEEKDAY_SLOTS,
  },

  wrapcast: {
    id: "wrapcast",
    name: "Farcaster",
    apiType: "wrapcast",
    category: "social",
    color: "#8a63d2",
    charLimit: 800,
    editor: "plain",
    thread: "thread",
    commentsMedia: true,
    analytics: false,
    needsDeveloperApp: true,
    connectMethods: ["sandbox", "credentials"],
    credentialFields: [
      {
        key: "mnemonic",
        label: "Signer / API key",
        type: "password",
        required: true,
        help: "Chave do signer Warpcast/Neynar. Não use a recovery phrase da carteira principal.",
      },
      { key: "fid", label: "FID", type: "string", required: false },
    ],
    media: {
      minAttachments: 0,
      maxAttachments: 2,
      images: true,
      videos: false,
      mixed: false,
      textOnly: true,
      note: "Somente imagens.",
    },
    settings: [text("channelKey", "Canal Farcaster")],
    defaultSlots: WEEKDAY_SLOTS,
  },

  telegram: {
    id: "telegram",
    name: "Telegram",
    apiType: "telegram",
    category: "messaging",
    color: "#229ed9",
    charLimit: 4096,
    editor: "markdown",
    thread: "comment",
    commentsMedia: true,
    analytics: false,
    needsDeveloperApp: true,
    connectMethods: ["sandbox", "credentials"],
    credentialFields: [
      {
        key: "botToken",
        label: "Token do bot",
        type: "password",
        required: true,
        help: "BotFather → /newbot.",
      },
      {
        key: "chatId",
        label: "Chat ID do canal ou grupo",
        type: "string",
        required: true,
        placeholder: "@seucanal ou -100…",
      },
    ],
    media: {
      minAttachments: 0,
      maxAttachments: 10,
      images: true,
      videos: true,
      mixed: true,
      textOnly: true,
      note: "Mensagem, foto, vídeo ou documento no chat configurado.",
    },
    settings: [
      text("chatId", "Chat ID (sobrescreve o da conta)"),
      select(
        "parseMode",
        "Parse mode",
        [opt("HTML", "HTML"), opt("MarkdownV2", "MarkdownV2"), opt("", "Texto puro")],
        "HTML",
      ),
      toggle("disableNotification", "Enviar silenciosamente"),
      toggle("protectContent", "Proteger conteúdo"),
    ],
    defaultSlots: WEEKDAY_SLOTS,
  },

  nostr: {
    id: "nostr",
    name: "Nostr",
    apiType: "nostr",
    category: "social",
    color: "#5c23d3",
    charLimit: 100_000,
    editor: "plain",
    thread: "none",
    commentsMedia: false,
    analytics: false,
    needsDeveloperApp: false,
    connectMethods: ["sandbox", "credentials"],
    credentialFields: [
      {
        key: "nsec",
        label: "Chave privada (nsec)",
        type: "password",
        required: true,
        help: "Apenas a nsec desta identidade de publicação. Cifrada em repouso.",
      },
      {
        key: "relays",
        label: "Relays (vírgula)",
        type: "text",
        required: false,
        placeholder: "wss://relay.damus.io",
      },
    ],
    media: {
      minAttachments: 0,
      maxAttachments: 4,
      images: true,
      videos: false,
      mixed: false,
      textOnly: true,
      note: "Nota kind 1. Sem settings por post.",
    },
    settings: [],
    defaultSlots: WEEKDAY_SLOTS,
  },

  vk: {
    id: "vk",
    name: "VK",
    apiType: "vk",
    category: "social",
    color: "#0077ff",
    charLimit: 2048,
    editor: "plain",
    thread: "comment",
    commentsMedia: true,
    analytics: false,
    needsDeveloperApp: true,
    connectMethods: ["sandbox", "oauth", "credentials"],
    oauth: {
      authorizeUrl: "https://id.vk.com/authorize",
      tokenUrl: "https://id.vk.com/oauth2/auth",
      scopes: ["wall", "photos", "offline"],
      pkce: true,
    },
    credentialFields: tokenFields([
      { key: "groupId", label: "ID do grupo", type: "string", required: false },
    ]),
    media: {
      minAttachments: 0,
      maxAttachments: 10,
      images: true,
      videos: true,
      mixed: true,
      textOnly: true,
      note: "Post no mural do usuário ou grupo.",
    },
    settings: [
      text("groupId", "ID do grupo (negativo = comunidade)"),
      toggle("fromGroup", "Publicar como grupo", true),
    ],
    defaultSlots: WEEKDAY_SLOTS,
  },

  medium: {
    id: "medium",
    name: "Medium",
    apiType: "medium",
    category: "blog",
    color: "#000000",
    charLimit: 100_000,
    editor: "markdown",
    thread: "none",
    commentsMedia: false,
    analytics: false,
    needsDeveloperApp: false,
    connectMethods: ["sandbox", "credentials"],
    credentialFields: [
      {
        key: "integrationToken",
        label: "Integration token",
        type: "password",
        required: true,
        help: "Medium Settings → Integration tokens.",
      },
    ],
    media: {
      minAttachments: 0,
      maxAttachments: 5,
      images: true,
      videos: false,
      mixed: false,
      textOnly: true,
      note: "Artigo markdown/HTML com título.",
    },
    settings: [
      text("title", "Título", { required: true, maxLength: 140 }),
      text("canonicalUrl", "URL canônica", { type: "url" }),
      text("tags", "Tags (máx. 5, vírgula)"),
      select(
        "publishStatus",
        "Status",
        [opt("public", "Público"), opt("draft", "Rascunho"), opt("unlisted", "Não listado")],
        "public",
      ),
      select(
        "license",
        "Licença",
        [
          opt("all-rights-reserved", "Todos os direitos"),
          opt("cc-40-by", "CC BY 4.0"),
          opt("cc-40-by-sa", "CC BY-SA 4.0"),
          opt("cc-40-by-nd", "CC BY-ND 4.0"),
          opt("cc-40-by-nc", "CC BY-NC 4.0"),
          opt("public-domain", "Domínio público"),
        ],
        "all-rights-reserved",
      ),
    ],
    defaultSlots: WEEKDAY_SLOTS,
  },

  devto: {
    id: "devto",
    name: "Dev.to",
    apiType: "devto",
    category: "blog",
    color: "#0a0a0a",
    charLimit: 100_000,
    editor: "markdown",
    thread: "none",
    commentsMedia: false,
    analytics: false,
    needsDeveloperApp: false,
    connectMethods: ["sandbox", "credentials"],
    credentialFields: [
      {
        key: "apiKey",
        label: "API key",
        type: "password",
        required: true,
        help: "dev.to Settings → Extensions → DEV API Keys.",
      },
    ],
    media: {
      minAttachments: 0,
      maxAttachments: 5,
      images: true,
      videos: false,
      mixed: false,
      textOnly: true,
      note: "Artigo markdown. Título obrigatório.",
    },
    settings: [
      text("title", "Título", { required: true, maxLength: 128 }),
      text("tags", "Tags (máx. 4, vírgula)"),
      text("canonicalUrl", "URL canônica", { type: "url" }),
      text("series", "Série"),
      toggle("published", "Publicar agora", true),
    ],
    defaultSlots: WEEKDAY_SLOTS,
  },

  hashnode: {
    id: "hashnode",
    name: "Hashnode",
    apiType: "hashnode",
    category: "blog",
    color: "#2962ff",
    charLimit: 10_000,
    editor: "markdown",
    thread: "none",
    commentsMedia: false,
    analytics: false,
    needsDeveloperApp: false,
    connectMethods: ["sandbox", "credentials"],
    credentialFields: [
      { key: "token", label: "Personal access token", type: "password", required: true },
      { key: "publicationId", label: "Publication ID", type: "string", required: true },
    ],
    media: {
      minAttachments: 0,
      maxAttachments: 5,
      images: true,
      videos: false,
      mixed: false,
      textOnly: true,
      note: "Artigo markdown com título.",
    },
    settings: [
      text("title", "Título", { required: true }),
      text("subtitle", "Subtítulo"),
      text("tags", "Tags (vírgula)"),
      text("canonicalUrl", "URL canônica", { type: "url" }),
      toggle("hideFromHashnodeFeed", "Ocultar do feed Hashnode"),
    ],
    defaultSlots: WEEKDAY_SLOTS,
  },

  wordpress: {
    id: "wordpress",
    name: "WordPress",
    apiType: "wordpress",
    category: "blog",
    color: "#21759b",
    charLimit: 100_000,
    editor: "html",
    thread: "none",
    commentsMedia: false,
    analytics: false,
    needsDeveloperApp: false,
    connectMethods: ["sandbox", "credentials"],
    credentialFields: [
      {
        key: "siteUrl",
        label: "URL do site",
        type: "url",
        required: true,
        placeholder: "https://blog.exemplo.com",
      },
      { key: "username", label: "Usuário", type: "string", required: true },
      {
        key: "applicationPassword",
        label: "Application password",
        type: "password",
        required: true,
        help: "Usuários → Perfil → Senhas de aplicativo.",
      },
    ],
    media: {
      minAttachments: 0,
      maxAttachments: 20,
      images: true,
      videos: true,
      mixed: true,
      textOnly: true,
      note: "HTML. Categorias e tags são IDs numéricos.",
    },
    settings: [
      text("title", "Título", { required: true }),
      select(
        "status",
        "Status",
        [
          opt("publish", "Publicar"),
          opt("draft", "Rascunho"),
          opt("pending", "Pendente"),
          opt("private", "Privado"),
        ],
        "publish",
      ),
      text("categories", "IDs de categorias (vírgula)"),
      text("tags", "IDs de tags (vírgula)"),
      text("excerpt", "Resumo"),
      text("featuredMedia", "ID da imagem destacada"),
      text("slug", "Slug"),
    ],
    defaultSlots: WEEKDAY_SLOTS,
  },

  listmonk: {
    id: "listmonk",
    name: "Listmonk",
    apiType: "listmonk",
    category: "other",
    color: "#0055d4",
    charLimit: 300_000,
    editor: "html",
    thread: "none",
    commentsMedia: false,
    analytics: false,
    needsDeveloperApp: false,
    connectMethods: ["sandbox", "credentials"],
    credentialFields: [
      { key: "url", label: "URL do Listmonk", type: "url", required: true },
      { key: "username", label: "Usuário", type: "string", required: true },
      { key: "password", label: "Senha", type: "password", required: true },
    ],
    media: {
      minAttachments: 0,
      maxAttachments: 10,
      images: true,
      videos: false,
      mixed: false,
      textOnly: true,
      note: "Campanha de newsletter HTML.",
    },
    settings: [
      text("name", "Nome da campanha", { required: true }),
      text("subject", "Assunto", { required: true }),
      text("listIds", "IDs das listas (vírgula)", { required: true }),
      text("fromEmail", "Remetente"),
      select(
        "contentType",
        "Tipo",
        [opt("html", "HTML"), opt("markdown", "Markdown"), opt("plain", "Texto")],
        "html",
      ),
      toggle("sendNow", "Enviar agora", true),
    ],
    defaultSlots: WEEKDAY_SLOTS,
  },

  moltbook: {
    id: "moltbook",
    name: "Moltbook",
    apiType: "moltbook",
    category: "community",
    color: "#ff6b35",
    charLimit: 300,
    editor: "plain",
    thread: "comment",
    commentsMedia: true,
    analytics: false,
    needsDeveloperApp: false,
    connectMethods: ["sandbox", "credentials"],
    credentialFields: [{ key: "apiKey", label: "API key", type: "password", required: true }],
    media: {
      minAttachments: 0,
      maxAttachments: 4,
      images: true,
      videos: false,
      mixed: false,
      textOnly: true,
      note: "Post curto na comunidade.",
    },
    settings: [text("community", "Comunidade")],
    defaultSlots: WEEKDAY_SLOTS,
  },

  whop: {
    id: "whop",
    name: "Whop",
    apiType: "whop",
    category: "community",
    color: "#ff5a00",
    charLimit: 50_000,
    editor: "markdown",
    thread: "comment",
    commentsMedia: true,
    analytics: false,
    needsDeveloperApp: true,
    connectMethods: ["sandbox", "oauth", "credentials"],
    oauth: {
      authorizeUrl: "https://whop.com/oauth",
      tokenUrl: "https://api.whop.com/v5/oauth/token",
      scopes: ["forum:write"],
      pkce: false,
    },
    credentialFields: tokenFields([
      { key: "experienceId", label: "Experience ID", type: "string", required: true },
    ]),
    media: {
      minAttachments: 0,
      maxAttachments: 10,
      images: true,
      videos: true,
      mixed: true,
      textOnly: true,
      note: "Post no fórum da experience.",
    },
    settings: [text("experienceId", "Experience ID"), text("title", "Título", { required: true })],
    defaultSlots: WEEKDAY_SLOTS,
  },

  skool: {
    id: "skool",
    name: "Skool",
    apiType: "skool",
    category: "community",
    color: "#f5c518",
    charLimit: 50_000,
    editor: "markdown",
    thread: "comment",
    commentsMedia: true,
    analytics: false,
    needsDeveloperApp: false,
    connectMethods: ["sandbox", "credentials"],
    credentialFields: [
      {
        key: "sessionCookie",
        label: "Cookie de sessão",
        type: "password",
        required: true,
        help: "Skool não tem OAuth público. Cole o cookie da sessão da comunidade.",
      },
      { key: "groupSlug", label: "Slug do grupo", type: "string", required: true },
    ],
    media: {
      minAttachments: 0,
      maxAttachments: 10,
      images: true,
      videos: true,
      mixed: true,
      textOnly: true,
      note: "Post na comunidade Skool.",
    },
    settings: [text("title", "Título", { required: true }), text("categoryId", "Categoria")],
    defaultSlots: WEEKDAY_SLOTS,
  },

  mewe: {
    id: "mewe",
    name: "MeWe",
    apiType: "mewe",
    category: "social",
    color: "#17313b",
    charLimit: 63_206,
    editor: "plain",
    thread: "none",
    commentsMedia: false,
    analytics: false,
    needsDeveloperApp: true,
    connectMethods: ["sandbox", "oauth", "credentials"],
    oauth: {
      authorizeUrl: "https://mewe.com/api/v3/oauth/authorize",
      tokenUrl: "https://mewe.com/api/v3/oauth/token",
      scopes: ["post"],
      pkce: false,
    },
    credentialFields: tokenFields(),
    media: {
      minAttachments: 0,
      maxAttachments: 10,
      images: true,
      videos: true,
      mixed: true,
      textOnly: true,
      note: "Sem partes extras (thread/comentário).",
    },
    settings: [],
    defaultSlots: WEEKDAY_SLOTS,
  },

  tumblr: {
    id: "tumblr",
    name: "Tumblr",
    apiType: "tumblr",
    category: "social",
    color: "#001935",
    charLimit: 32_768,
    editor: "html",
    thread: "none",
    commentsMedia: false,
    analytics: false,
    needsDeveloperApp: true,
    connectMethods: ["sandbox", "oauth", "credentials"],
    oauth: {
      authorizeUrl: "https://www.tumblr.com/oauth2/authorize",
      tokenUrl: "https://api.tumblr.com/v2/oauth2/token",
      scopes: ["basic", "write", "offline_access"],
      pkce: true,
    },
    credentialFields: tokenFields([
      { key: "blogIdentifier", label: "Blog (hostname)", type: "string", required: true },
    ]),
    media: {
      minAttachments: 0,
      maxAttachments: 30,
      images: true,
      videos: true,
      mixed: false,
      textOnly: true,
      maxVideos: 1,
      note: "Até 30 imagens ou 1 vídeo. Sem partes extras.",
    },
    settings: [
      text("blogIdentifier", "Blog"),
      select(
        "type",
        "Tipo",
        [
          opt("text", "Texto"),
          opt("photo", "Foto"),
          opt("quote", "Citação"),
          opt("link", "Link"),
          opt("video", "Vídeo"),
        ],
        "text",
      ),
      text("tags", "Tags (vírgula)"),
      select(
        "state",
        "Estado",
        [opt("published", "Publicado"), opt("draft", "Rascunho"), opt("queue", "Fila")],
        "published",
      ),
    ],
    defaultSlots: WEEKDAY_SLOTS,
  },
};

export const PLATFORM_LIST: PlatformDefinition[] = SOCIAL_PLATFORM_IDS.map((id) => PLATFORMS[id]);

export function getPlatform(id: string): PlatformDefinition | null {
  if ((SOCIAL_PLATFORM_IDS as readonly string[]).includes(id)) {
    return PLATFORMS[id as SocialPlatformId];
  }
  return null;
}

export function requirePlatform(id: string): PlatformDefinition {
  const platform = getPlatform(id);
  if (platform === null) {
    throw new Error(`Rede social desconhecida: ${id}`);
  }
  return platform;
}

export function defaultSettingsFor(id: SocialPlatformId): Record<string, unknown> {
  const out: Record<string, unknown> = {};
  for (const field of PLATFORMS[id].settings) {
    if (field.defaultValue !== undefined) out[field.key] = field.defaultValue;
  }
  return out;
}

export function isOAuthConfigured(
  id: SocialPlatformId,
  apps: Partial<Record<SocialPlatformId, { clientId: string; clientSecret: string }>>,
): boolean {
  const platform = PLATFORMS[id];
  if (!platform.connectMethods.includes("oauth")) return false;
  const app = apps[id];
  return app !== undefined && app.clientId.length > 0 && app.clientSecret.length > 0;
}
